/**
 * Shared UI design tokens — ported from iOS (`AppColors.swift`, `GlassCard.swift`, `PetFeature.swift`).
 *
 * Purpose: one place where the web app's visual language lives so every screen
 * stays in sync with the iOS app. When iOS changes, we change these tokens.
 */

// ---- Category tints (iOS SwiftUI system-color equivalents) ----------------
// Tuned to render well on both light and dark surfaces. Tailwind 400 shades.

export const categoryTints = {
  // Main feature tiles
  healthInsights: '#60A5FA', // blue
  carePlan: '#C084FC',       // purple
  petExpenses: '#4ADE80',    // green
  appointments: '#FB923C',   // orange
  messages: '#22D3EE',       // cyan/teal
  careTeam: '#FB7185',       // rose/pink

  // Quick action tints (reuses + indigo/pink)
  scanReceipt: '#4ADE80',    // green
  logWeight: '#60A5FA',      // blue
  symptom: '#FB7185',        // rose
  logMedication: '#FB923C',  // orange

  // Activity row / misc
  labResults: '#818CF8',     // indigo
  vaccinations: '#22D3EE',   // cyan
  weightTrend: '#60A5FA',    // blue
  vetVisit: '#22D3EE',       // cyan

  // Pet hero / monitoring state
  monitoring: '#FBBF24',     // amber (matches iOS MONITORING chip glow)
  amber: '#FBBF24',
} as const;

export type CategoryTint = keyof typeof categoryTints;

// ---- Radius scale (iOS uses 24 for cards, 18 for smaller tiles) ----------
export const uiRadius = {
  pill: 999,
  chip: 12,
  tile: 18,     // Quick actions
  card: 24,     // Feature tiles, hero card, doses/activity containers
  photo: '50%', // round pet photos
} as const;

// ---- Glow shadow — iOS dual-shadow pattern -------------------------------
// iOS: .shadow(color: tint.opacity(0.16→0.22), radius: 18, x: 0, y: 8)
//    + .shadow(color: tint.opacity(0.08→0.11), radius: 3, x: 0, y: 1)
// Translated to CSS box-shadow (two stacked shadows).

export const glowShadow = (hex: string, dark = false): string => {
  // Convert #RRGGBB to rgba with the iOS-ported opacity values.
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const o1 = dark ? 0.22 : 0.16;
  const o2 = dark ? 0.11 : 0.08;
  return [
    `0 8px 18px rgba(${r}, ${g}, ${b}, ${o1})`,
    `0 1px 3px rgba(${r}, ${g}, ${b}, ${o2})`,
  ].join(', ');
};

// ---- Glass surface backgrounds --------------------------------------------
// iOS GlassCard uses a translucent material over tinted bg. On web we pick
// tinted dark backgrounds with subtle gradient sheen.

export const glassSurface = {
  light: {
    /** Dark mode base card (near-black green-tint, matches iOS #1F211F) */
    cardBgDark: '#1F211F',
    /** Light-mode card (near-white) */
    cardBgLight: '#FFFFFF',
    /** Inner overlay gradient applied on top of card for glass sheen */
    sheenDark: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
    sheenLight: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%)',
    /** Card border */
    borderDark: 'rgba(255,255,255,0.08)',
    borderLight: 'rgba(0,0,0,0.06)',
  },
} as const;

// ---- Icon chip (circle containing the feature icon) -----------------------
// iOS: Circle().fill(tint.opacity(0.18)) frame(width:44, height:44),
//      Image ... foregroundStyle(tint)

export const iconChip = {
  sizes: {
    sm: 34,  // quick actions
    md: 36,  // activity rows
    lg: 40,  // dose rows (rounded rect, not circle)
    xl: 44,  // feature tiles
  },
  bgOpacity: 0.18,   // 18% tint behind icon
  iconSizeRatio: 0.5, // icon size ~= chipSize * 0.5
} as const;

// ---- Spacing scale (iOS uses 12 for grid gaps, 22 for section spacing) ---
export const uiSpacing = {
  sectionGap: 22,
  gridGap: 12,
  cardPadding: 16,
  heroCardPadding: 18,
  rowPadding: 12,
  activityRowPadding: 14,
} as const;
